import * as THREE from "three";

/** Set15 P3 (§3.7) — the unit-space 3D terrain geometry.  A CHUNK_N×CHUNK_N surface grid
 *  (vertices at `(i/(N-1), j/(N-1), 0)`, so the `uv` attribute lands EXACTLY on the fragment's
 *  lattice mapping) PLUS a duplicated perimeter ring flagged `aSkirt = 1`.  The vertex shader
 *  (MATERIAL_VERTEX_GLSL) pushes skirt verts down by a chunk-proportional amount, backing the
 *  LOD crack at a coarse/fine boundary.
 *
 *  One shared instance is reused by every 3D chunk (the per-chunk transform is position+scale
 *  on the mesh — the geometry is never mutated).  Skirt winding is moot for visibility because
 *  the 3D material uses `side: DoubleSide` (the far-side skirt walls face away from the camera
 *  and must still render).
 *
 *  Exporting the builder (rather than inlining it in ChunkMeshPool) lets the Node lane assert
 *  the vertex/skirt/index structure directly (plan §9). */
export function buildTerrainGeo3D(CHUNK_N = 33): THREE.BufferGeometry {
	const N = CHUNK_N;
	const positions: number[] = [];
	const uvs: number[] = [];
	const skirtFlags: number[] = [];
	for (let j = 0; j < N; j++) {
		for (let i = 0; i < N; i++) {
			positions.push(i / (N - 1), j / (N - 1), 0);
			uvs.push(i / (N - 1), j / (N - 1));
			skirtFlags.push(0);
		}
	}
	const surfIdx = (i: number, j: number)=>j * N + i;
	// The perimeter in CCW order (viewed from +z): bottom, right, top, left.  Each entry is a
	// surface index; the skirt ring DUPLICATES the surface perimeter (same XZ, flagged aSkirt = 1)
	// so the shader can drop it independently of the surface edge.
	const perimeter: number[] = [];
	for (let i = 0; i < N; i++) perimeter.push(surfIdx(i, 0)); // bottom
	for (let j = 1; j < N; j++) perimeter.push(surfIdx(N - 1, j)); // right
	for (let i = N - 2; i >= 0; i--) perimeter.push(surfIdx(i, N - 1)); // top
	for (let j = N - 2; j >= 1; j--) perimeter.push(surfIdx(0, j)); // left
	// Append the skirt ring vertices (one per perimeter surface vertex).
	const skirtStart = N * N;
	for (const s of perimeter) {
		const px = positions[s * 3 + 0], py = positions[s * 3 + 1];
		positions.push(px, py, 0);
		uvs.push(px, py);
		skirtFlags.push(1);
	}
	const skirtIdx = (k: number)=>skirtStart + k;
	// Surface triangles (CCW from +z, so the top face points up).
	const indices: number[] = [];
	for (let j = 0; j < N - 1; j++) {
		for (let i = 0; i < N - 1; i++) {
			const a = surfIdx(i, j), b = surfIdx(i + 1, j), c = surfIdx(i + 1, j + 1), d = surfIdx(i, j + 1);
			indices.push(a, b, c, a, c, d);
		}
	}
	// Skirt quads: between each adjacent perimeter surface vertex pair and their skirt
	// counterparts.  DoubleSide makes the winding moot (see the doc comment).
	for (let k = 0; k < perimeter.length; k++) {
		const k2 = (k + 1) % perimeter.length;
		const a = perimeter[k], b = perimeter[k2]; // surface edge
		const sa = skirtIdx(k), sb = skirtIdx(k2); // skirt edge (dropped in the shader)
		indices.push(a, b, sa, b, sb, sa);
	}
	const geo = new THREE.BufferGeometry();
	geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
	geo.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
	geo.setAttribute("aSkirt", new THREE.Float32BufferAttribute(skirtFlags, 1));
	geo.setIndex(indices);
	return geo;
}
